import React, { useState, useEffect } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Progress,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BrandAvatar } from "../../components/BrandAvatar";
import { Link as RouterLink } from "react-router-dom";
import {
  FiActivity,
  FiBell,
  FiBookmark,
  FiBookOpen,
  FiCalendar,
  FiLayers,
  FiMail,
  FiUsers,
  FiEye,
  FiEdit3,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import * as FeedbackService from "../../services/feedbackService";
import {
  fetchNotifications,
  linkForNotification,
  markAllRead,
  markRead,
} from "../../services/notificationsService";
import {
  savedCount,
  COLLECTIONS_EVENT,
} from "../../services/collectionsService";

const FeedbackWidget = FeedbackService.FeedbackWidget || (() => null);

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// One notification, in the unread list or in the history. Unread rows are
// tinted and bold; read rows are quiet, because in the history they are only
// there as a record.
const NotifRow = ({ n, cardTitle, cardMuted, onClick }) => (
  <Box
    as={RouterLink}
    to={linkForNotification(n)}
    onClick={() => onClick(n)}
    display="block"
    bg={n.is_read ? "transparent" : "blue.50"}
    borderWidth="1px"
    borderColor="border.default"
    borderRadius="md"
    px={3}
    py={2}
    cursor="pointer"
    transition="all 0.12s ease"
    _hover={{
      bg: "surface.canvas",
      borderColor: "gwbi.300",
      textDecoration: "none",
    }}
  >
    <Text
      fontSize="xs"
      fontWeight={n.is_read ? "medium" : "bold"}
      color={cardTitle}
      noOfLines={2}
    >
      {n.message || n.type}
    </Text>
    <Text fontSize="0.6rem" color={cardMuted} mt={0.5}>
      {relTime(n.created_at)}
    </Text>
  </Box>
);

export const ProfileSidebar = ({
  profile,
  user,
  t,
  cardStyles,
  completeness,
  stringifyList,
  connectionsCount,
  dmUnreadTotal = 0,
  myPostsCount,
  myArticlesCount,
  onViewProfile,
}) => {
  const { cardBg, cardText, cardTitle, cardMuted, cardBorder } = cardStyles;
  const [profileViews, setProfileViews] = useState(null);
  const [savedItems, setSavedItems] = useState(0);
  useEffect(() => {
    const update = () => setSavedItems(savedCount());
    update();
    window.addEventListener(COLLECTIONS_EVENT, update);
    return () => window.removeEventListener(COLLECTIONS_EVENT, update);
  }, []);
  const [paperViews, setPaperViews] = useState(null);
  const [eventsCount, setEventsCount] = useState(null);
  // The card shows what still needs attention: unread only. Everything that
  // has been read is one click away, under History, rather than padding the
  // card with things already dealt with.
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [history, setHistory] = useState({
    open: false,
    loading: false,
    loaded: false,
    items: [],
  });

  const listOf = (data) =>
    Array.isArray(data?.notifications)
      ? data.notifications
      : Array.isArray(data)
      ? data
      : [];

  useEffect(() => {
    let cancelled = false;
    fetchNotifications({ unreadOnly: true, limit: 20 })
      .then((data) => {
        if (cancelled || !data) return;
        const items = listOf(data);
        const unread = Number.isFinite(data.unread_count)
          ? data.unread_count
          : items.length;
        setNotifs({ items, unread });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id, profile?.id, profile?.user_id]);

  // Mark a notification read on click (optimistic). Read means handled, so it
  // leaves the card immediately and reappears in the history below.
  const markNotifRead = (n) => {
    if (!n || n.is_read) return;
    setNotifs((prev) => ({
      items: prev.items.filter((it) => it.id !== n.id),
      unread: Math.max(0, prev.unread - 1),
    }));
    setHistory((prev) =>
      prev.loaded
        ? {
            ...prev,
            items: prev.items.map((it) =>
              it.id === n.id ? { ...it, is_read: true } : it
            ),
          }
        : prev
    );
    markRead(n.id).catch(() => {});
  };

  const handleMarkAllRead = () => {
    if (!notifs.items.length) return;
    const cleared = notifs.items.map((it) => ({ ...it, is_read: true }));
    setNotifs({ items: [], unread: 0 });
    setHistory((prev) =>
      prev.loaded
        ? {
            ...prev,
            items: prev.items.map((it) => {
              const hit = cleared.find((c) => c.id === it.id);
              return hit ? { ...it, is_read: true } : it;
            }),
          }
        : prev
    );
    markAllRead().catch(() => {});
  };

  // History is fetched the first time it is opened, not on page load: most
  // visits never ask for it. The fetch stays outside the state updater, which
  // React is free to run more than once.
  const toggleHistory = () => {
    const opening = !history.open;
    setHistory((prev) => ({ ...prev, open: opening }));
    if (!opening || history.loading) return;
    // Refetch on every open so the list is current, but only spin the first
    // time — after that the cached rows stay on screen while it refreshes.
    setHistory((prev) => ({ ...prev, loading: !prev.loaded }));
    fetchNotifications({ limit: 50 })
      .then((data) =>
        setHistory((cur) => ({
          ...cur,
          loading: false,
          loaded: true,
          items: listOf(data),
        }))
      )
      .catch(() =>
        setHistory((cur) => ({ ...cur, loading: false, loaded: true }))
      );
  };

  useEffect(() => {
    const uid = user?.id || profile?.id || profile?.user_id;
    if (!uid) return;
    const headers = {
      "Content-Type": "application/json",
      "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
    };
    const authToken = localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken");
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

    fetch(`https://api.globalwineinstitute.org/analytics/profile-views?user_id=${uid}`, { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setProfileViews(data.total ?? 0); })
      .catch(() => {});

    fetch(`https://api.globalwineinstitute.org/analytics/paper-views?user_id=${uid}`, { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setPaperViews(data.total ?? 0); })
      .catch(() => {});

    fetch(`https://api.globalwineinstitute.org/conferences_gwbi?my_rsvps=true&limit=200`, { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const items = data?.events || data?.items || (Array.isArray(data) ? data : []);
        setEventsCount(items.length);
      })
      .catch(() => { setEventsCount(0); });
  }, [user?.id, profile?.id, profile?.user_id]);

  const displayedRole = user?.role || profile?.role || "—";
  const isAdmin = (user?.role || profile?.role) === "admin";
  const unreadBadge =
    dmUnreadTotal > 0 ? (dmUnreadTotal > 99 ? "99+" : dmUnreadTotal) : null;

  return (
    <VStack spacing={4} align="stretch">
      <Box
        bg={cardBg}
        color={cardText}
        borderRadius="md"
        overflow="hidden"
        boxShadow="md"
      >
        <Box
          h="80px"
          bgImage={`url('${profile?.banner_url || "/banner.webp"}')`}
          bgPos="center"
          bgSize="cover"
        />
        <Box p={4} position="relative">
          <Box role="button" onClick={onViewProfile}>
            <BrandAvatar
              name={
                `${(profile?.first_name || "").trim()} ${(
                  profile?.last_name || ""
                ).trim()}`.trim() || t("dashboard.your_name")
              }
              src={profile?.avatar_url || undefined}
              boxSize={"90px"}
              position="absolute"
              top="-50px"
              border="3px solid white"
              _hover={{ boxShadow: "lg", cursor: "pointer" }}
            />
          </Box>
          <Box mt={8}>
            <Text textStyle="cardTitle" color={cardTitle}>
              {`${(profile?.first_name || "").trim()} ${(
                profile?.last_name || ""
              ).trim()}`.trim() || t("dashboard.your_name")}
            </Text>
            <Text fontSize="sm" color={cardMuted}>
              {profile?.headline || "Add a headline"}
            </Text>
            <Text fontSize="sm" color={cardMuted}>
              {profile?.type || "—"}
            </Text>
            <Text fontSize="sm" color={cardMuted}>
              {stringifyList(profile?.regions) || "—"}
            </Text>
            <Text fontSize="sm" color={cardMuted}>
              {displayedRole}
            </Text>
            {isAdmin && (
              <Button
                as={RouterLink}
                to="/administration"
                size="sm"
                mt={3}
                colorScheme="gwbi"
                variant="ghost"
              >
                Admin
              </Button>
            )}
            <Box mt={3}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" color={cardMuted} opacity={0.8}>
                  {t("dashboard.profile_completeness")}
                </Text>
                <Text fontSize="xs" color={cardMuted} opacity={0.8}>
                  {completeness.pct}%
                </Text>
              </HStack>
              <Progress
                value={completeness.pct}
                size="2xs"
                borderRadius="full"
                colorScheme="gray"
                bg="whiteAlpha.200"
                sx={{
                  "& > div": {
                    background:
                      "linear-gradient(to right, rgba(255,255,255,0.6), rgba(255,255,255,0.2))",
                  },
                }}
              />
              {completeness.next && completeness.next.length > 0 && (
                <Text
                  mt={1}
                  fontSize="xs"
                  color={cardMuted}
                  opacity={0.6}
                  noOfLines={1}
                  fontStyle="italic"
                >
                  {t("dashboard.next")} {completeness.next.join(", ")}
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <FeedbackWidget
        mode="card"
        showLauncher={false}
        cardBackground={cardBg}
        cardBorderColor={cardBorder}
        cardTextColor={cardText}
        prefill={{
          name: `${(profile?.first_name || "").trim()} ${(
            profile?.last_name || ""
          ).trim()}`.trim(),
        }}
        hideFields={["email"]}
        labelOverride="Feedback & Suggestions"
        launchButtonProps={{
          size: "sm",
          colorScheme: "gwbi",
          variant: "solid",
          fontSize: "xs",
          leftIcon: undefined,
          rightIcon: undefined,
          children: "Feedback",
        }}
      />

      <Box
        bg={cardBg}
        color={cardText}
        borderRadius="md"
        overflow="hidden"
        boxShadow="md"
        p={4}
      >
        <VStack spacing={3} align="stretch">
          <HStack>
            <Icon as={FiActivity} boxSize={5} />
            <Text textStyle="cardTitle" color={cardTitle}>
              {t("dashboard.statistics")}
            </Text>
          </HStack>
          <HStack justify="space-between">
            <HStack>
              <Icon as={FiEye} boxSize={4} />
              <Text fontSize="sm" fontWeight="bold">
                {t("dashboard.profile_viewers")}
              </Text>
            </HStack>
            <Text fontSize="sm" fontWeight="bold">
              {profileViews != null ? profileViews : "…"}
            </Text>
          </HStack>
          <HStack justify="space-between">
            <HStack>
              <Icon as={FiEye} boxSize={4} />
              <Text fontSize="sm" fontWeight="bold">
                {t("dashboard.paper_viewers") || "Paper Viewers"}
              </Text>
            </HStack>
            <Text fontSize="sm" fontWeight="bold">
              {paperViews != null ? paperViews : "…"}
            </Text>
          </HStack>
        </VStack>
      </Box>

      <Box
        bg={cardBg}
        color={cardText}
        borderRadius="md"
        overflow="hidden"
        boxShadow="md"
        p={4}
      >
        <VStack spacing={3} align="stretch">
          <HStack>
            <Icon as={FiLayers} boxSize={5} />
            <Text textStyle="cardTitle" color={cardTitle}>
              {t("dashboard.collections")}
            </Text>
          </HStack>
          <Box as="button" onClick={onViewProfile} style={{ width: "100%" }}>
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiEdit3} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold">
                  {t("dashboard.posts")}
                </Text>
              </HStack>
              <Text fontSize="sm" fontWeight="bold">
                {myPostsCount}
              </Text>
            </HStack>
          </Box>
          <Box as="button" onClick={onViewProfile} style={{ width: "100%" }}>
  <HStack justify="space-between">
    <HStack>
      <Icon as={FiBookOpen} boxSize={4} />
      <Text fontSize="sm" fontWeight="bold">
        {t("dashboard.publications")}
      </Text>
    </HStack>
    <Text fontSize="sm" fontWeight="bold">
      {myArticlesCount}
    </Text>
  </HStack>
</Box>

          <Box as={RouterLink} to="/collections">
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiBookmark} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold">
                  {t("dashboard.saved", "Saved")}
                </Text>
              </HStack>
              <Text fontSize="sm" fontWeight="bold">
                {savedItems}
              </Text>
            </HStack>
          </Box>
          <Box as={RouterLink} to="/connections">
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiUsers} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold">
                  {t("dashboard.contacts", "Contacts")}
                </Text>
              </HStack>
              <Text fontSize="sm" fontWeight="bold">
                {connectionsCount != null ? connectionsCount : "…"}
              </Text>
            </HStack>
          </Box>
          <Box as={RouterLink} to="/inbox">
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiMail} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold">
                  {t("dashboard.messages", "Messages")}
                </Text>
              </HStack>
              {unreadBadge ? (
                <Box
                  bg="gwbi.500"
                  color="white"
                  borderRadius="full"
                  minW="18px"
                  h="18px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  fontWeight="bold"
                  px={1.5}
                >
                  {unreadBadge}
                </Box>
              ) : (
                <Text fontSize="sm" fontWeight="bold" color={cardMuted}>
                  0
                </Text>
              )}
            </HStack>
          </Box>
          <Box as={RouterLink} to="/events">
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiCalendar} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold">
                  {t("dashboard.events")}
                </Text>
              </HStack>
              <Text fontSize="sm" fontWeight="bold">
                {eventsCount != null ? eventsCount : "…"}
              </Text>
            </HStack>
          </Box>
        </VStack>
      </Box>

      <Box
        bg={cardBg}
        color={cardText}
        borderRadius="md"
        overflow="hidden"
        boxShadow="md"
        p={4}
      >
        <HStack mb={3} justify="space-between">
          <HStack>
            <Icon as={FiBell} boxSize={5} />
            <Text textStyle="cardTitle" color={cardTitle}>
              {t("dashboard.notifications", "Notifications")}
            </Text>
          </HStack>
          {notifs.unread > 0 && (
            <Badge
              colorScheme="red"
              borderRadius="full"
              px={2}
              py={0.5}
              fontSize="0.65rem"
            >
              {notifs.unread > 99 ? "99+" : notifs.unread}
            </Badge>
          )}
        </HStack>
        {notifs.items.length === 0 ? (
          <Text fontSize="xs" color={cardMuted}>
            {t("dashboard.no_notifications", "You're all caught up.")}
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {notifs.items.slice(0, 6).map((n) => (
              <NotifRow
                key={n.id}
                n={n}
                cardTitle={cardTitle}
                cardMuted={cardMuted}
                onClick={markNotifRead}
              />
            ))}
            {notifs.items.length > 6 && (
              <Text fontSize="0.6rem" color={cardMuted}>
                {t("dashboard.more_unread", "+{{count}} more unread", {
                  count: notifs.items.length - 6,
                })}
              </Text>
            )}
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gwbi"
              alignSelf="flex-start"
              onClick={handleMarkAllRead}
            >
              {t("dashboard.mark_all_read", "Mark all as read")}
            </Button>
          </VStack>
        )}

        <Button
          size="sm"
          variant="ghost"
          colorScheme="gwbi"
          w="100%"
          mt={3}
          rightIcon={
            <Icon as={history.open ? FiChevronUp : FiChevronDown} boxSize={4} />
          }
          onClick={toggleHistory}
        >
          {history.open
            ? t("dashboard.hide_history", "Hide history")
            : t("dashboard.show_history", "Show history")}
        </Button>

        {history.open && (
          <Box
            mt={2}
            maxH="260px"
            overflowY="auto"
            pr={1}
            borderTopWidth="1px"
            borderColor="border.default"
            pt={2}
          >
            {history.loading ? (
              <HStack justify="center" py={3}>
                <Spinner size="sm" color="gwbi.600" />
              </HStack>
            ) : history.items.length === 0 ? (
              <Text fontSize="xs" color={cardMuted}>
                {t("dashboard.no_history", "Nothing here yet.")}
              </Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {history.items.map((n) => (
                  <NotifRow
                    key={n.id}
                    n={n}
                    cardTitle={cardTitle}
                    cardMuted={cardMuted}
                    onClick={markNotifRead}
                  />
                ))}
              </VStack>
            )}
          </Box>
        )}
      </Box>

    </VStack>
  );
};
